import type { AxiosError } from 'axios';

import { EntityType } from '~/enums/EntityType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { everest } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';
import { bytesToBlob, coerceFileType } from '~/utils/files';

import { judgmentResultActivity } from './mocks/tickets';

type PathId = string | number;
type UserId = string | number;

interface TurnTrackingPayload {
  partyId?: Nullable<Uuid>;
}

interface DocumentVersionResponse {
  fileType: string;
  [key: string]: unknown;
}

export async function createTicket({
  intakeForm,
  counterpartyFile,
  turnTracking,
  referenceId,
  contractRecordUniqueId,
}: {
  intakeForm: unknown;
  counterpartyFile?: Nullable<Blob>;
  turnTracking?: Nullable<TurnTrackingPayload>;
  referenceId?: Nullable<string>;
  contractRecordUniqueId?: Nullable<string>;
}) {
  const formData = new FormData();
  formData.append('intakeForm', JSON.stringify(intakeForm));
  if (counterpartyFile) {
    formData.append('file', counterpartyFile);
  }
  if (turnTracking?.partyId) {
    formData.append('turnTracking', JSON.stringify(turnTracking));
  }
  if (contractRecordUniqueId) {
    formData.append('previousContractRecordUniqueId', contractRecordUniqueId);
  }
  const params: { reference_id?: string } = {};
  if (referenceId) {
    params.reference_id = referenceId;
  }
  return await everest.post('/tickets/', formData, { params });
}

export async function uploadSignedDocument({
  ticketId,
  text = '',
  file,
  signerIds,
  isKeepingUploadedFilename = false,
}: {
  ticketId: Uuid;
  text?: string;
  file: Blob;
  signerIds: (string | number)[];
  isKeepingUploadedFilename?: boolean;
}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('text', text);
  formData.append(
    'isKeepingUploadedFilename',
    String(isKeepingUploadedFilename),
  );
  signerIds.forEach((signerId) =>
    formData.append('recipientIds', String(signerId)),
  );

  return await everest.post(`/tickets/${ticketId}/upload-signed`, formData);
}

export async function partialUpdateTicket(ticketId: PathId, payload: unknown) {
  return await everest.patch(`/tickets/${ticketId}`, payload);
}

// TODO - remove `disableRedirect` when opinionated redirection is handled in `createService`
export async function deleteTicket(
  { ticketId }: { ticketId: PathId },
  disableRedirect = false,
) {
  return await everest.remove(`/tickets/${ticketId}`, {
    data: { disableRedirect },
  });
}

export async function patchTicketStage({ ticketId }: { ticketId: Uuid }) {
  return await everest.post(`/tickets/${ticketId}/next-stage`);
}

export async function backToReview({
  ticketId,
  shouldResetApprovals = true,
}: {
  ticketId: Uuid;
  shouldResetApprovals?: boolean;
}) {
  return await everest.post(`/tickets/${ticketId}/back-to-review/`, {
    resetApprovals: shouldResetApprovals,
  });
}

export async function addNewReminder({
  ticketId,
  approvalId,
}: {
  ticketId: Uuid;
  approvalId: Uuid;
}) {
  return await everest.post(
    `/tickets/${ticketId}/judgment-results/${approvalId}/remind/`,
  );
}

export async function shareTicketDocument({
  data,
  documentId,
  versionId,
}: {
  data: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    body?: string;
  };
  documentId: Uuid;
  versionId: Uuid;
}) {
  return await everest.post(
    `/documents/${documentId}/versions/${versionId}/share`,
    {
      emailsTo: data.to,
      emailsCc: data.cc,
      emailsBcc: data.bcc,
      subject: data.subject,
      body: data.body,
    },
  );
}

export interface ShareTicketDocumentAttachmentsData {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  attachments: unknown;
  uploadedAttachments?: Blob[];
  turnTracking?: Nullable<TurnTrackingPayload>;
}

export async function shareTicketDocumentAttachments({
  data,
  ticketId,
}: {
  data: ShareTicketDocumentAttachmentsData;
  ticketId: PathId;
}) {
  const {
    to,
    cc,
    bcc,
    subject,
    body,
    attachments,
    uploadedAttachments,
    turnTracking,
  } = data;
  const formData = new FormData();
  to?.forEach((toEmail) => formData.append('emailsTo', toEmail));
  cc?.forEach((ccEmail) => formData.append('emailsCc', ccEmail));
  bcc?.forEach((bccEmail) => formData.append('emailsBcc', bccEmail));
  formData.append('body', body);
  formData.append('subject', subject);
  formData.append('onPlatformAttachments', JSON.stringify(attachments));
  if (turnTracking?.partyId) {
    formData.append('turnTracking', JSON.stringify(turnTracking));
  }
  uploadedAttachments?.forEach((uploadAttachment) =>
    formData.append('uploadedAttachments', uploadAttachment),
  );
  return await everest.post(`/tickets/${ticketId}/share`, formData);
}

export async function uploadNewDocumentVersion({
  documentId,
  file,
  text,
  isCounterparty = false,
  isKeepingUploadedFilename = true,
  isGenerated = false,
  referenceVersionId = null,
  targetVersionId = null,
  userIds,
  turnTracking,
}: {
  documentId: Uuid;
  file: Blob;
  text: string;
  isCounterparty?: boolean;
  isKeepingUploadedFilename?: boolean;
  isGenerated?: boolean;
  referenceVersionId?: Nullable<Uuid>;
  targetVersionId?: Nullable<Uuid>;
  userIds?: Nullable<UserId[]>;
  turnTracking?: Nullable<TurnTrackingPayload>;
}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('text', text);
  formData.append('isCounterparty', String(isCounterparty));
  formData.append(
    'isKeepingUploadedFilename',
    String(isKeepingUploadedFilename),
  );
  formData.append('isGenerated', String(isGenerated));
  if (referenceVersionId) {
    formData.append('referenceVersionId', referenceVersionId);
  }
  if (targetVersionId) {
    formData.append('targetVersionId', targetVersionId);
  }
  userIds?.forEach((userId) => formData.append('userIds', String(userId)));
  if (turnTracking?.partyId) {
    formData.append('turnTracking', JSON.stringify(turnTracking));
  }

  return await everest.post(`/documents/${documentId}/versions`, formData);
}

export async function patchTicketApproval({
  ticketId,
  approvalId,
  status,
  note,
}: {
  ticketId: Uuid;
  approvalId: Uuid;
  status: TicketStatusType;
  note?: string;
}) {
  const data = { status, comment: note };
  return await everest.patch(
    `/tickets/${ticketId}/judgment-results/${approvalId}/`,
    data,
  );
}

export async function reassignApproval({
  ticketId,
  issueId,
  oldAssigneeId,
  newAssigneeId,
  entityType,
}: {
  ticketId: Uuid;
  issueId: Uuid;
  oldAssigneeId: UserId;
  newAssigneeId: Nullable<UserId>;
  entityType: string;
}) {
  return await everest.patch(
    `/tickets/${ticketId}/issues/${issueId}/reassign`,
    {
      oldUserId: oldAssigneeId,
      assigneeUserId: newAssigneeId,
      entityType,
    },
  );
}

export async function reassignCoordinator({
  ticketId,
  stageId,
  coordinatorId,
}: {
  ticketId: Uuid;
  stageId: Uuid;
  coordinatorId: Nullable<UserId>;
}) {
  return await everest.patch(`/tickets/${ticketId}/stages/${stageId}`, {
    userId: coordinatorId,
  });
}

export async function getActivities({
  entityId,
  entityType,
  status,
}: {
  entityId: Uuid;
  entityType: EntityType;
  status?: string;
}) {
  const params = { entityId, entityType };

  switch (entityType) {
    case EntityType.Ticket:
      return everest.get('/activities', { params });
    default:
      return judgmentResultActivity(status);
  }
}

export async function getTicketDocumentVersions(documentId: Uuid) {
  const documentVersions = await everest.get<DocumentVersionResponse[]>(
    `/documents/${documentId}/versions`,
  );
  return documentVersions.map((documentVersion) => ({
    ...documentVersion,
    fileType: coerceFileType(documentVersion.fileType),
  }));
}

export async function getTicketDocumentFiles({
  documentId,
  versionId,
}: {
  documentId: Uuid;
  versionId: Uuid;
}) {
  const original = await everest.get<Blob>(
    `/documents/${documentId}/versions/${versionId}/download/original`,
    { responseType: 'blob' },
  );

  return {
    orig: bytesToBlob(original, original.type),
    html: null, // deprecated. Tickets no longer maintain an html preview.
  };
}

export async function downloadTicketDocument({
  documentId,
  versionId,
  isWorkingDraft = false,
}: {
  documentId: Uuid;
  versionId: Uuid;
  isWorkingDraft?: boolean;
}) {
  const original = await everest.get<Blob>(
    `/documents/${documentId}/versions/${versionId}/download-file`,
    {
      params: {
        isWorkingDraft,
      },
      responseType: 'blob',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    },
  );

  return bytesToBlob(original, original.type);
}

export async function downloadTicketDocumentWithoutActivityLog({
  documentId,
  versionId,
  isWorkingDraft = false,
}: {
  documentId: Uuid;
  versionId: Uuid;
  isWorkingDraft?: boolean;
}) {
  const docFile = await everest.get<Blob>(
    `/documents/${documentId}/versions/${versionId}/download/${
      isWorkingDraft ? 'working_draft' : 'original'
    }`,
    {
      responseType: 'blob',
    },
  );

  return bytesToBlob(docFile, docFile.type);
}

export async function patchTicketStatus({
  entityId,
  ticketId,
  status,
  ...params
}: {
  entityId: Uuid;
  ticketId: Uuid;
  status: TicketStatusType;
  comment?: string;
  [key: string]: unknown;
}) {
  let statusAction;
  switch (status) {
    case TicketStatusType.Approved:
      statusAction = 'approve';
      break;
    case TicketStatusType.Rejected:
      statusAction = 'reject';
      break;
    default:
      statusAction = 'unsupported action';
  }

  const url = `/tickets/${ticketId}/issues/${entityId}/${statusAction}`;
  const resp = await everest.patch(url, params);
  const { activities, approvals } = resp;

  return {
    id: entityId,
    status: resp.status,
    activities,
    approvals,
    comment: params.comment,
  };
}

export async function resetApproval(
  ticketId: Uuid,
  issueId: Uuid,
  comment: string,
  entityType: string,
  assignees: UserId[],
) {
  return await everest.patch(`/tickets/${ticketId}/issues/${issueId}/reset/`, {
    comment,
    entityType,
    assignees,
  });
}

export async function resetApprovals(
  ticketId: Uuid,
  stageId: Uuid,
  comment: string,
) {
  return await everest.patch(
    `/tickets/${ticketId}/stages/${stageId}/reset-approvals/`,
    { comment },
  );
}

export async function getTicketParticipants({ ticketId }: { ticketId: Uuid }) {
  return await everest.get(`/tickets/${ticketId}/participants/`);
}

export async function removeTicketParticipant(
  ticketId: Uuid,
  participantId: UserId,
) {
  return await everest.remove(
    `/tickets/${ticketId}/participants/${participantId}/`,
  );
}

export function addTicketParticipants({
  ticketId,
  userIds,
}: {
  ticketId: Uuid;
  userIds: UserId[];
}) {
  return everest.post(`/tickets/${ticketId}/participants`, { userIds });
}

export async function downloadTicketAttachment(attachmentId: Uuid) {
  const original = await everest.get<Blob>(`/attachments/${attachmentId}/`, {
    responseType: 'blob',
  });

  return bytesToBlob(original, original.type);
}

async function uploadAtttachment(file: Blob, fieldId?: string) {
  const formData = new FormData();
  formData.append('file', file);
  if (fieldId) {
    formData.append('intake_form_field_id', fieldId);
  }
  return await everest.post('/attachments/', formData); // subject to location change if Pilot not everest
}

export async function uploadAtttachments<T>(
  files: File[],
  callback: (data: T[]) => void,
  errorHandler: (error: AxiosError<{ data: string }>, fileName: string) => void,
  fieldId?: string,
) {
  const promises = files.map((file) => {
    return uploadAtttachment(file, fieldId).catch((error) => {
      errorHandler(error as AxiosError<{ data: string }>, file.name);
    });
  });

  return (
    everest
      .concurrentRequests<T | undefined>(promises)
      // rejected promises will return undefined, so we filter them out
      .then((data) =>
        callback(data.filter((item): item is T => item !== undefined)),
      )
  );
}

export function deleteTicketDocumentVersion(documentId: Uuid, versionId: Uuid) {
  return everest.remove(`/documents/${documentId}/versions/${versionId}`);
}

export function getTicketAttachments(ticketId: PathId) {
  return everest.get(`/tickets/${ticketId}/attachments/`);
}

export function updateRecipients({
  ticketId,
  updatedRecipients,
}: {
  ticketId: Uuid;
  updatedRecipients: { approvalIds: (Uuid | number)[]; deleteCC: boolean }[];
}) {
  const payload = updatedRecipients.map((item, index) => {
    return {
      newCC: false,
      judgmentResultId: item.approvalIds[0],
      position: index,
      deleteCC: item.deleteCC,
    };
  });
  return everest.patch(`/tickets/${ticketId}/signers`, { data: payload });
}
